import { useEffect, type RefObject } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';

type Options = {
  enabled: boolean;
  isSaving: boolean;
  savedRef: RefObject<boolean>;
  backPath: string;
  onDiscard: () => void;
};

export default function useCloneLeaveGuard({ enabled, isSaving, savedRef, backPath, onDiscard }: Options) {
  const navigate = useNavigate();
  // Read the ref when navigation is requested: Save may navigate before the
  // render that removes the draft, and must not open a discard confirmation.
  const blocker = useBlocker(() => enabled && !savedRef.current);

  useEffect(() => {
    if (!enabled) return undefined;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (savedRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [enabled, savedRef]);

  return {
    isLeaveDialogOpen: blocker.state === 'blocked',
    handleBack: () => navigate(backPath),
    cancelLeave: () => {
      if (blocker.state === 'blocked') blocker.reset();
    },
    confirmLeave: () => {
      if (isSaving || blocker.state !== 'blocked') return;
      onDiscard();
      blocker.proceed();
    },
  };
}
